/**
 * GraphQL for the 501c3 determination-letter upload on the Manage Account page.
 *
 * Three-step handoff, mirroring the CSV import in `integrationQueries.ts`:
 *   1. `rescueGet501c3UploadUrl` — ask the backend for a short-lived presigned
 *      PUT URL, then PUT the PDF bytes directly to S3.
 *   2. `rescueSubmit501c3Document` — once the PUT succeeds, hand the object key
 *      off to be filed for review. Returns the new document row (PENDING).
 *   3. `rescueCurrent501c3Document` — read the current document + review status
 *      back on page load.
 *
 * Documents are scoped to the logged-in user, not the org: in a rescue with
 * several portal logins, each user sees only the letters they uploaded.
 */

import { gql } from '@apollo/client';

/**
 * Review state of a submitted document. `SUPERSEDED` exists on the backend
 * enum but never reaches the portal — `rescueCurrent501c3Document` returns the
 * most recent non-superseded row.
 */
export type RescueDocumentStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/** Mirrors the `RescueDocument` GQL type (the fields the card renders). */
export interface RescueDocument {
  id: string;
  status: RescueDocumentStatus;
  originalFileName: string;
  sizeBytes: number;
  uploadedAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
}

const rescueDocumentFields = gql`
  fragment Rescue501c3Fields on RescueDocument {
    id
    status
    originalFileName
    sizeBytes
    uploadedAt
    reviewedAt
    rejectionReason
  }
`;

/**
 * The caller's current 501c3 document, or null if they've never submitted one.
 *
 * Deliberately does **not** select `downloadUrl`: it's a non-null field that
 * signs a fresh S3 URL on every read, and the card only ever displays the file
 * name — selecting it would sign a URL nothing consumes.
 */
export const rescueCurrent501c3DocumentQuery = gql`
  query rescueCurrent501c3Document {
    rescueCurrent501c3Document {
      ...Rescue501c3Fields
    }
  }
  ${rescueDocumentFields}
`;

export interface RescueCurrent501c3DocumentData {
  rescueCurrent501c3Document: RescueDocument | null;
}

/**
 * Request a presigned S3 PUT URL for the determination letter. The returned
 * `url` is short-lived; the client must PUT the body with the `Content-Type`
 * the `contentType` enum resolves to (`APPLICATION_PDF` → `application/pdf`) —
 * S3 enforces an exact header match against the signed request. `key` is the
 * resulting object key, passed on to `rescueSubmit501c3Document`.
 */
export const rescueGet501c3UploadUrlQuery = gql`
  query rescueGet501c3UploadUrl($input: RescueGet501c3UploadUrlInput!) {
    rescueGet501c3UploadUrl(input: $input) {
      url
      key
      expiration
    }
  }
`;

/** File format the client will upload. PDF is the only accepted format. */
export type Rescue501c3ContentType = 'APPLICATION_PDF';

export interface RescueGet501c3UploadUrlData {
  rescueGet501c3UploadUrl: {
    url: string;
    key: string;
    expiration: string;
  };
}

/**
 * File a freshly-uploaded letter for review. Pass the `key` returned by
 * `rescueGet501c3UploadUrl` (after the S3 PUT succeeds). Not idempotent —
 * calling twice supersedes the first row and re-queues, which is also how a
 * rescue replaces a REJECTED letter.
 */
export const rescueSubmit501c3DocumentMutation = gql`
  mutation rescueSubmit501c3Document($input: RescueSubmit501c3DocumentInput!) {
    rescueSubmit501c3Document(input: $input) {
      ...Rescue501c3Fields
    }
  }
  ${rescueDocumentFields}
`;

export interface RescueSubmit501c3DocumentVariables {
  input: {
    /** S3 object key returned by `rescueGet501c3UploadUrl`. */
    uploadedFileKey: string;
    /** Original file name, for display in the portal and admin review. */
    fileName: string;
  };
}

export interface RescueSubmit501c3DocumentData {
  rescueSubmit501c3Document: RescueDocument;
}
